"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useFinancialDisclosuresModal } from "@/hooks/useFinancialDisclosuresModal";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import type { Row } from "@tanstack/react-table";

type FinancialDisclosure = {
  id: string;
  personName: string;
  year: number;
  disclosureType: string;
  office: string;
  filingDate: Date;
  documentMediaId: string | null;
};

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const disclosure = row.original as FinancialDisclosure;
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);
  const setDisclosureId = useFinancialDisclosuresModal(
    (s) => s.setDisclosureId,
  );
  const setIsModalOpen = useFinancialDisclosuresModal((s) => s.setIsModalOpen);
  const setDefaultValues = useFinancialDisclosuresModal(
    (s) => s.setDefaultValues,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem
          onClick={() => {
            setDisclosureId(disclosure.id);
            setDefaultValues({
              personName: disclosure.personName,
              year: disclosure.year,
              disclosureType: disclosure.disclosureType as
                | "campaign"
                | "conflict-of-interest",
              office: disclosure.office,
              filingDate: new Date(disclosure.filingDate),
              documentMediaId: disclosure.documentMediaId || undefined,
            });
            setIsModalOpen(true);
          }}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setDisclosureId(null); // Clear ID to create duplicate
            setDefaultValues({
              personName: `${disclosure.personName} (Copy)`,
              year: disclosure.year,
              disclosureType: disclosure.disclosureType as
                | "campaign"
                | "conflict-of-interest",
              office: disclosure.office,
              filingDate: new Date(),
              documentMediaId: disclosure.documentMediaId || undefined,
            });
            setIsModalOpen(true);
          }}
        >
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setDeleteId(disclosure.id);
          }}
        >
          Delete
          <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
